import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEventById } from '../services/eventService';
import { checkInToEvent, getEventCheckIns, getCheckInByUserAndEvent } from '../services/checkInService';
import { Event, CheckIn } from '../types';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../hooks/useCategories';
import CheckInFeed from '../components/CheckInFeed';
import PhotoGallery from '../components/PhotoGallery';
import PhotoUpload from '../components/PhotoUpload';
import { logAnalyticsEvent } from '../firebase/config';

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, isAdmin } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [hasCheckedIn, setHasCheckedIn] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshPhotos, setRefreshPhotos] = useState(0);
  const { t } = useTranslation();
  const { getCategoryName } = useCategories();

  const fetchEventData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const eventData = await getEventById(id);
      if (eventData) {
        setEvent(eventData);
        const checkInsData = await getEventCheckIns(id);
        setCheckIns(checkInsData);
        if (user) {
          const userCheckIn = await getCheckInByUserAndEvent(id, user.id);
          setHasCheckedIn(!!userCheckIn);
        }
      } else {
        setError(t('eventDetail.notFound'));
      }
    } catch {
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEventData(); }, [id, user, t]);

  const handleCheckIn = async () => {
    if (!event?.id || !user) return;
    try {
      setCheckingIn(true);
      setError(null);
      await checkInToEvent(event.id, user.id, user.name, user.photoURL, checkInMessage.trim() || undefined);
      setHasCheckedIn(true);
      setCheckInMessage('');
      await fetchEventData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCheckingIn(false);
    }
  };

  const handleBuyTickets = () => {
    if (!event) return;
    logAnalyticsEvent('select_promotion', {
      promotion_id: event.id, promotion_name: event.title,
      creative_name: 'ticket_purchase', creative_slot: 'event_detail',
    });
    if (event.ticketUrl) {
      window.open(event.ticketUrl, '_blank');
    } else if (!isAuthenticated) {
      navigate('/login', { state: { from: `/events/${id}` } });
    } else {
      alert(`${quantity} ticket(s) reservado(s) para ${event.title}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-r-transparent" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="bg-error-container text-on-error-container px-6 py-4 rounded-xl font-body text-body-md">
          {error || t('eventDetail.notFound')}
        </div>
        <button onClick={() => navigate('/events')} className="bg-primary text-on-primary px-6 py-3 rounded-lg font-headline text-label-md hover:brightness-110 transition-all">
          {t('events.title')}
        </button>
      </div>
    );
  }

  const formattedDate = format(parseISO(event.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isPastEvent = parseISO(event.date) < today;

  return (
    <div className="bg-background min-h-screen">

      {/* ── HERO: dark band, info left / image right ── */}
      <section className="bg-inverse-surface text-inverse-on-surface">
        <div className="max-w-container mx-auto px-4 md:px-margin-desktop py-10 flex flex-col md:flex-row gap-8 items-start">

          {/* Left: info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 bg-primary text-on-primary rounded-full font-headline text-label-sm">
                {getCategoryName(event.category)}
              </span>
              {isPastEvent && (
                <span className="px-3 py-1 bg-error text-on-error rounded-full font-headline text-label-sm">
                  Evento Finalizado
                </span>
              )}
            </div>

            <h1 className="font-headline text-headline-lg-mobile md:text-headline-lg text-white mb-6 leading-tight">
              {event.title}
            </h1>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-surface-variant">
                <span className="material-symbols-outlined text-primary text-[20px]">calendar_today</span>
                <span className="font-body text-body-md">{formattedDate}</span>
              </div>
              <div className="flex items-center gap-3 text-surface-variant">
                <span className="material-symbols-outlined text-primary text-[20px]">schedule</span>
                <span className="font-body text-body-md">{event.time}</span>
              </div>
              <div className="flex items-center gap-3 text-surface-variant">
                <span className="material-symbols-outlined text-primary text-[20px]">location_on</span>
                <span className="font-body text-body-md">{event.venue} · {event.city}</span>
              </div>
              {event.price !== undefined && (
                <div className="flex items-center gap-3 text-surface-variant">
                  <span className="material-symbols-outlined text-primary text-[20px]">confirmation_number</span>
                  <span className="font-body text-body-md">
                    A partir de <strong className="text-white">R$ {event.price}</strong>
                  </span>
                </div>
              )}
            </div>

            {/* CTA mobile */}
            {!isPastEvent && (
              <button
                onClick={handleBuyTickets}
                className="mt-8 md:hidden w-full py-4 bg-primary text-on-primary rounded-xl font-headline text-label-md hover:brightness-110 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-[20px]">confirmation_number</span>
                {event.ticketUrl ? t('eventDetail.buyTicketsExternal') : t('eventDetail.buyTickets')}
              </button>
            )}
          </div>

          {/* Right: cover image — aspect 1600/838 */}
          <div className="w-full md:w-[420px] flex-shrink-0 rounded-2xl overflow-hidden shadow-2xl">
            <div className="aspect-[1600/838] w-full">
              <img
                src={event.imageUrl}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── BODY: description left / ticket sidebar right ── */}
      <div className="max-w-container mx-auto px-4 md:px-margin-desktop py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Description */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="font-headline text-headline-sm text-on-surface mb-4">{t('eventDetail.aboutEvent')}</h2>
              <p className="font-body text-body-md text-on-surface-variant whitespace-pre-wrap leading-relaxed">
                {event.description}
              </p>
            </div>

            {/* Past event photos */}
            {isPastEvent && (
              <div className="border-t border-outline-variant pt-8">
                {isAdmin && (
                  <div className="mb-6">
                    <PhotoUpload eventId={event.id} onUploadComplete={() => setRefreshPhotos(p => p + 1)} />
                  </div>
                )}
                <PhotoGallery eventId={event.id} eventTitle={event.title} key={refreshPhotos} />
              </div>
            )}

            {/* Check-ins */}
            {!isPastEvent && (
              <div className="border-t border-outline-variant pt-8">
                <h3 className="font-headline text-headline-sm text-on-surface mb-4">
                  {t('eventDetail.checkIn.title', { count: checkIns.length })}
                </h3>
                {isAuthenticated ? (
                  <>
                    {!hasCheckedIn && (
                      <textarea
                        value={checkInMessage}
                        onChange={(e) => setCheckInMessage(e.target.value)}
                        placeholder={t('eventDetail.checkIn.commentPlaceholder')}
                        rows={2}
                        className="w-full border border-outline-variant rounded-xl p-3 font-body text-body-md bg-surface focus:outline-none focus:border-primary mb-3"
                      />
                    )}
                    <button
                      onClick={handleCheckIn}
                      disabled={hasCheckedIn || checkingIn}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-headline text-label-md transition-all active:scale-95 ${
                        hasCheckedIn
                          ? 'bg-tertiary-fixed text-on-tertiary-fixed cursor-not-allowed'
                          : checkingIn
                          ? 'bg-surface-container text-on-surface-variant cursor-wait'
                          : 'bg-primary text-on-primary hover:brightness-110'
                      }`}
                    >
                      <span className="material-symbols-outlined text-[18px]">how_to_reg</span>
                      {hasCheckedIn ? t('eventDetail.checkIn.alreadyCheckedIn') : checkingIn ? t('eventDetail.checkIn.processing') : t('eventDetail.checkIn.button')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => navigate('/login', { state: { from: `/events/${id}` } })}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary text-on-primary rounded-lg font-headline text-label-md hover:brightness-110 transition-all"
                  >
                    <span className="material-symbols-outlined text-[18px]">how_to_reg</span>
                    {t('nav.login')}
                  </button>
                )}
                {checkIns.length > 0 && (
                  <div className="mt-6">
                    <CheckInFeed checkIns={checkIns} onCheckInUpdate={fetchEventData} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Ticket sidebar — desktop, future events only */}
          {!isPastEvent && (
            <div className="hidden lg:block">
              <div className="sticky top-24 bg-surface-container-low p-6 rounded-2xl border border-outline-variant">
                <h3 className="font-headline text-label-sm text-on-surface-variant uppercase tracking-widest mb-5">
                  {t('eventDetail.getTickets')}
                </h3>

                {event.price !== undefined && (
                  <div className="mb-5">
                    <p className="font-body text-label-sm text-on-surface-variant mb-1">{t('eventDetail.price')}</p>
                    <p className="font-headline text-headline-md text-primary">R$ {event.price}</p>
                  </div>
                )}

                {!event.ticketUrl && event.availableTickets !== undefined && (
                  <div className="mb-4">
                    <label htmlFor="quantity" className="font-headline text-label-md text-on-surface-variant block mb-2">
                      {t('eventDetail.quantity')}
                    </label>
                    <select
                      id="quantity"
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                      className="w-full border border-outline-variant rounded-lg py-2.5 px-3 font-body text-body-md bg-surface focus:outline-none focus:border-primary"
                    >
                      {[...Array(Math.min(10, event.availableTickets))].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                )}

                {event.availableTickets !== undefined && (
                  <div className="flex items-center gap-2 text-on-surface-variant font-body text-body-md mb-5">
                    <span className="material-symbols-outlined text-[18px]">group</span>
                    <span>{t('eventDetail.ticketsAvailable', { count: event.availableTickets })}</span>
                  </div>
                )}

                <div className="bg-primary-fixed rounded-xl p-4 mb-5">
                  <p className="font-headline text-label-sm text-on-primary-fixed-variant uppercase tracking-widest">CÓDIGO</p>
                  <p className="font-headline text-headline-md text-primary">MTIX</p>
                </div>

                {error && (
                  <div className="mb-4 bg-error-container text-on-error-container px-3 py-2 rounded-lg font-body text-label-sm">{error}</div>
                )}

                <button
                  onClick={handleBuyTickets}
                  disabled={event.availableTickets === 0}
                  className={`w-full py-4 rounded-xl font-headline text-label-md flex items-center justify-center gap-2 transition-all active:scale-95 ${
                    event.availableTickets === 0
                      ? 'bg-surface-container text-on-surface-variant cursor-not-allowed'
                      : 'bg-primary text-on-primary hover:brightness-110 shadow-lg'
                  }`}
                >
                  <span className="material-symbols-outlined text-[20px]">
                    {event.ticketUrl ? 'open_in_new' : 'confirmation_number'}
                  </span>
                  {event.availableTickets === 0
                    ? t('eventDetail.soldOut')
                    : event.ticketUrl
                    ? t('eventDetail.buyTicketsExternal')
                    : isAuthenticated
                    ? t('eventDetail.buyTickets')
                    : t('nav.login')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
